import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthCtx = createContext(null);
import { resetHomestayNameCache } from "../components/PageHeader";

const TOKEN_KEY = "meos_token";

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);       // user object
  const [status, setStatus] = useState("loading"); // loading | authed | guest

  const token = localStorage.getItem(TOKEN_KEY);

  // Axios interceptor
  useEffect(() => {
    const id = api.interceptors.request.use((config) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    const respId = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401 && localStorage.getItem(TOKEN_KEY)) {
          localStorage.removeItem(TOKEN_KEY);
          if (window.location.pathname !== "/login") {
            window.location.href = "/login";
          }
        }
        return Promise.reject(err);
      }
    );
    return () => {
      api.interceptors.request.eject(id);
      api.interceptors.response.eject(respId);
    };
  }, []);

  useEffect(() => {
    if (!token) {
      setStatus("guest");
      return;
    }
    api.get("/auth/me")
      .then((r) => { setUser(r.data); setStatus("authed"); })
      .catch(() => { localStorage.removeItem(TOKEN_KEY); setStatus("guest"); });
  }, [token]);

  const login = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    localStorage.setItem(TOKEN_KEY, r.data.access_token);
    setUser(r.data.user);
    setStatus("authed");
    return r.data.user;
  };

  // Generic session setter — used by demo login (already has token+user from backend).
  const setSession = (accessToken, userObj) => {
    resetHomestayNameCache();
    localStorage.setItem(TOKEN_KEY, accessToken);
    setUser(userObj);
    setStatus("authed");
  };

  const logout = () => {
    resetHomestayNameCache();
    localStorage.removeItem(TOKEN_KEY);
    setUser(null);
    setStatus("guest");
  };

  const isOwner = user?.role === "owner";
  const isSuperAdmin = !!user?.is_super_admin;

  return (
    <AuthCtx.Provider value={{ user, status, login, logout, setSession, isOwner, isSuperAdmin, token }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
export const getToken = () => localStorage.getItem(TOKEN_KEY);
